/**
 * registry.tsx — block `type` -> renderer dispatch for the View Protocol,
 * plus the three CONTAINER blocks (`dashboard` / `modal` / `tabs`) that
 * recurse back into this same dispatch for their children. Keeping the
 * recursive container renderers in this file (rather than in RichBlocks.tsx)
 * avoids a circular import between the dispatch table and the leaf renderers.
 *
 * `renderBlock` is a plain switch on the `type` discriminator — an
 * unrecognised type falls through to `default: null`, never a dynamic /
 * unchecked property lookup. `SafeBlock` additionally wraps every dispatch
 * (the top-level list in RichResponse AND every container's children) in a
 * per-block React error boundary, so a single malformed/edge-case payload
 * degrades to nothing instead of taking down the whole chat surface — the
 * "never crash" contract for the whole renderer.
 */
import { Component, useState, type ReactNode } from 'react';

import { Card, CardGrid, Modal, Tabs, type TabItem } from '@/ui';

import {
  CalloutBlockView,
  CardsBlockView,
  ChartBlockView,
  DividerBlockView,
  FormBlockView,
  GridBlockView,
  KpiBlockView,
  KpiGroupBlockView,
  MarkdownBlockView,
  TableBlockView,
  TextBlockView,
} from './RichBlocks';
import type { Block, DashboardBlock, ModalBlock, RichAction, TabsBlock } from './blocks';
import { useT } from '@/i18n';

export interface RichRenderContext {
  onAction?: (action: RichAction) => void;
  /** Nesting depth for dashboard/modal/tabs recursion; capped so a
   * pathological/circular view can never runaway-render. */
  depth: number;
}

const MAX_DEPTH = 6;

function isBoxlessWidget(block: Block): boolean {
  // kpi / kpi_group already render as their own bordered tile(s); wrapping
  // them in a second Card inside a dashboard would double-box them.
  return block.type === 'kpi' || block.type === 'kpi_group';
}

// ---------------------------------------------------------------------------
// Container blocks
// ---------------------------------------------------------------------------

function DashboardBlockView({ block, ctx }: { block: DashboardBlock; ctx: RichRenderContext }): ReactNode {
  const widgets = block.widgets ?? [];
  if (widgets.length === 0) return null;
  const childCtx: RichRenderContext = { ...ctx, depth: ctx.depth + 1 };
  return (
    <div className="flex flex-col gap-3">
      {block.title && <h3 className="text-base font-semibold text-foreground">{block.title}</h3>}
      <CardGrid>
        {widgets.map((widget, i) =>
          isBoxlessWidget(widget) ? (
            <div key={i}>
              <SafeBlock block={widget} ctx={childCtx} />
            </div>
          ) : (
            <Card key={i}>
              <SafeBlock block={widget} ctx={childCtx} />
            </Card>
          ),
        )}
      </CardGrid>
    </div>
  );
}

function ModalBlockView({ block, ctx }: { block: ModalBlock; ctx: RichRenderContext }): ReactNode {
  const { t: tx } = useT();
  const [open, setOpen] = useState(true);
  const childCtx: RichRenderContext = { ...ctx, depth: ctx.depth + 1 };
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="self-start rounded-token border border-border bg-surface px-3 py-1.5 text-xs font-medium text-foreground shadow-elevation-1 transition hover:bg-surface-2"
      >{tx('shell.features.copilotRichRegistry.reopen')}{' '}{block.title || tx('shell.features.copilotRichRegistry.theWindow')} »
      </button>
    );
  }
  return (
    <Modal title={block.title || undefined} onClose={() => setOpen(false)}>
      <div className="flex flex-col gap-3">
        {(block.blocks ?? []).map((child, i) => (
          <SafeBlock key={i} block={child} ctx={childCtx} />
        ))}
      </div>
    </Modal>
  );
}

function TabsBlockView({ block, ctx }: { block: TabsBlock; ctx: RichRenderContext }): ReactNode {
  const { t: tx } = useT();
  const tabs = block.tabs ?? [];
  const items: TabItem[] = tabs.map((t, i) => ({ key: String(i), label: t.label || tx('shell.features.copilotRichRegistry.tabV', { v: i + 1 }) }));
  const [active, setActive] = useState('0');
  if (items.length === 0) return null;
  const activeTab = tabs[Number(active)] ?? tabs[0];
  const childCtx: RichRenderContext = { ...ctx, depth: ctx.depth + 1 };
  return (
    <div className="flex flex-col gap-3">
      <Tabs tabs={items} value={active} onChange={setActive} aria-label={tx('shell.features.copilotRichRegistry.tabs')} />
      <div className="flex flex-col gap-3">
        {(activeTab?.blocks ?? []).map((child, i) => (
          <SafeBlock key={i} block={child} ctx={childCtx} />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

export function renderBlock(block: Block, ctx: RichRenderContext): ReactNode {
  if (ctx.depth > MAX_DEPTH || block == null || typeof block !== 'object') return null;
  switch (block.type) {
    case 'text':
      return <TextBlockView block={block} />;
    case 'markdown':
      return <MarkdownBlockView block={block} />;
    case 'kpi':
      return <KpiBlockView block={block} />;
    case 'kpi_group':
      return <KpiGroupBlockView block={block} />;
    case 'table':
      return <TableBlockView block={block} />;
    case 'cards':
      return <CardsBlockView block={block} />;
    case 'grid':
      return <GridBlockView block={block} />;
    case 'chart':
      return <ChartBlockView block={block} />;
    case 'form':
      return <FormBlockView block={block} onAction={ctx.onAction} />;
    case 'dashboard':
      return <DashboardBlockView block={block} ctx={ctx} />;
    case 'modal':
      return <ModalBlockView block={block} ctx={ctx} />;
    case 'tabs':
      return <TabsBlockView block={block} ctx={ctx} />;
    case 'divider':
      return <DividerBlockView />;
    case 'callout':
      return <CalloutBlockView block={block} />;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Per-block error boundary
// ---------------------------------------------------------------------------

interface BoundaryProps {
  children: ReactNode;
}
interface BoundaryState {
  failed: boolean;
}

class BlockErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { failed: false };

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.warn('[RichResponse] a view-protocol block failed to render — dropped, not fatal.', error);
    }
  }

  render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * Renders ONE block defensively: an unknown `type` -> `null` (via
 * `renderBlock`'s switch default), a thrown render error -> `null` (via the
 * boundary). Used both by `RichResponse` for the top-level list and by every
 * container block (`dashboard` / `modal` / `tabs`) for its children, so the
 * "never crash" guarantee holds at every nesting depth.
 */
export function SafeBlock({ block, ctx }: { block: Block; ctx: RichRenderContext }): ReactNode {
  return <BlockErrorBoundary>{renderBlock(block, ctx)}</BlockErrorBoundary>;
}
